import { signIn } from 'next-auth/react';
import { useEffect } from 'react';

import { useMetrics } from '../../../hooks/useMetrics';
import useSignIn from '../../../hooks/useSignIn';
import { AUTH_EVENTS, setAuthPending } from '../../../utils/authFunnel';
import BackgroundBlobs from '../../_molecules/BackgroundBlobs/BackgroundBlobs';

type NewProvider = 'google' | 'clever' | 'classlink' | 'feide';

type Props = {
  newAuth: boolean;
  /** New direct-OAuth provider id (used when the US_AUTH flag is on). */
  newProvider: NewProvider;
  /** Legacy Azure B2C self-join provider id (used when the flag is off). */
  azureProvider: string;
  domainHint: string;
};

/**
 * Shared SSO self-join entry point (Clever / ClassLink / Feide / Google).
 *
 * Flag ON: redirect through the new direct-OAuth provider. On the callback the
 * NextAuth jwt handler calls /v0.5/auth/{provider}SelfJoin (Basic auth,
 * server-side) which provisions the User + UserIdentity + tenant and syncs the
 * provider's roster — the "self-join" semantics, while still matching existing
 * users.
 *
 * Flag OFF: redirect through the legacy Azure B2C self-join policy.
 */
const SsoSelfJoinRedirect = ({
  newAuth,
  newProvider,
  azureProvider,
  domainHint,
}: Props) => {
  const { signIn: b2cSignIn } = useSignIn();
  const metrics = useMetrics(true);

  useEffect(() => {
    if (newAuth) {
      // Funnel: a federated method was chosen via a dedicated SSO entry page. These
      // pages have no mode toggle, so treat them as 'login' intent — isNewUser on
      // the resolved session still reveals signups (incl. signup-via-login).
      metrics.logEvent(AUTH_EVENTS.methodSelected, {
        mode: 'login',
        method: 'oauth',
        provider: newProvider,
        entry: 'ssoPage',
      });
      setAuthPending({ mode: 'login', method: 'oauth', provider: newProvider });
      // Mark this as a provider self-join (roster-sync) flow so the NextAuth
      // callback calls /v0.5/auth/{provider}SelfJoin instead of the generic
      // federated exchange used by the in-form "continue with" tiles.
      document.cookie = 'curipod_auth_flow=selfjoin; path=/; max-age=600; samesite=lax';
      signIn(newProvider, { callbackUrl: '/' });
    } else {
      b2cSignIn({
        provider: azureProvider,
        callbackUrl: '/',
        authParams: { domain_hint: domainHint },
      });
    }
  }, [newAuth, newProvider, azureProvider, domainHint, b2cSignIn, metrics]);

  return (
    <>
      <BackgroundBlobs />
    </>
  );
};

export default SsoSelfJoinRedirect;
